import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { getActiveWorldConfig } from '@gamecodex/world-config'
import { useT, LocaleLink } from '@/i18n'
import { AccessibilityControls } from './AccessibilityControls'

/** Routes where the player is at a game table — the footer collapses there so it
 * doesn't steal room from the tabletop. Path is locale-prefixed (`/en/demo`), so
 * strip the leading locale segment before matching. */
function isTabletop(pathname: string): boolean {
  const rest = pathname.replace(/^\/[^/]+/, '')
  return rest === '/demo' || rest.startsWith('/play')
}

export function Footer() {
  const { pathname } = useLocation()
  const tabletop = isTabletop(pathname)
  // Accordion: in tabletop mode the footer body is collapsed by default (and
  // toggleable); everywhere else it's always shown.
  const [open, setOpen] = useState(false)
  const showBody = tabletop ? open : true
  const t = useT()
  const { branding, worldName } = getActiveWorldConfig()

  return (
    <footer className="relative z-10 mt-16 border-t border-blood-900/60 bg-obsidian-500/80">
      {tabletop && (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="flex w-full items-center justify-center gap-1.5 py-2 font-ui text-[11px] uppercase tracking-title text-bone-dim transition-colors hover:text-gold"
        >
          {open ? <ChevronDown size={13} aria-hidden /> : <ChevronUp size={13} aria-hidden />}
          {open ? t('layout.footer.hideLinks') : t('layout.footer.showLinks')}
        </button>
      )}
      {showBody && (
        <div className="mx-auto grid w-full gap-8 px-4 py-10 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <img src={branding.images.logo} alt={worldName} className="h-10 w-auto" />
            <p className="mt-2 font-body text-sm text-bone-dim">{t('layout.footer.tagline')}</p>
          </div>
          <nav aria-label={t('layout.footer.explore')}>
            <h4 className="title-gothic mb-3 text-sm text-bone-muted">{t('layout.footer.explore')}</h4>
            <ul className="space-y-1 font-ui text-sm text-bone-dim">
              <li><LocaleLink to="/encyclopedia" className="hover:text-gold">{t('layout.footer.encyclopedia')}</LocaleLink></li>
              <li><LocaleLink to="/world" className="hover:text-gold">{t('layout.footer.world')}</LocaleLink></li>
              <li><LocaleLink to="/play" className="hover:text-gold">{t('layout.footer.playVtt')}</LocaleLink></li>
              <li><LocaleLink to="/books" className="hover:text-gold">{t('layout.footer.books')}</LocaleLink></li>
            </ul>
          </nav>
          <nav aria-label={t('layout.footer.legal')}>
            <h4 className="title-gothic mb-3 text-sm text-bone-muted">{t('layout.footer.legal')}</h4>
            <ul className="space-y-1 font-ui text-sm text-bone-dim">
              {/* Copyright (WOGD LLC) is stated on the Terms page, not in the footer (decision 2026-11-01). */}
              <li><LocaleLink to="/legal/terms" className="hover:text-gold">{t('layout.footer.terms')}</LocaleLink></li>
              <li><LocaleLink to="/legal/privacy" className="hover:text-gold">{t('layout.footer.privacy')}</LocaleLink></li>
              <li><LocaleLink to="/legal/canon-release" className="hover:text-gold">{t('layout.footer.canonRelease')}</LocaleLink></li>
              <li><LocaleLink to="/legal/ai" className="hover:text-gold">{t('layout.footer.aiPolicy')}</LocaleLink></li>
              <li><LocaleLink to="/legal/cookies" className="hover:text-gold">{t('layout.footer.cookies')}</LocaleLink></li>
            </ul>
          </nav>
          <div>
            <h4 className="title-gothic mb-3 text-sm text-bone-muted">{t('layout.footer.accessibility')}</h4>
            <AccessibilityControls />
          </div>
        </div>
      )}
    </footer>
  )
}
